import { useAuth } from './use-auth';
import { useBoundWalletGuard } from './use-bound-wallet-guard';
import { useModelStore } from '@/client/store/model-store';
import { truncateWallet } from '@/client/utils/wallet-address';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

/**
 * Gate before any action that needs a wallet (trade, deposit).
 *
 * Funnels the user to the right next step instead of a silent dead-end:
 *   - not signed in        -> open the login picker
 *   - signed in via DID,
 *     no wallet yet         -> connect a wallet (loginWallet)
 *   - wrong wallet active   -> block + prompt switching to the bound wallet
 *   - bound wallet connected -> proceed (returns true)
 *
 * Usage: const gate = useTradeGate(); ... if (!gate()) return; // proceed
 */
export const useTradeGate = () => {
  const { t } = useTranslation();
  // walletConnected comes from the auth context (wagmi in prod, demo flag in
  // mock). The did-auth store's flag was mock-only and never set in real
  // builds, which shoved SIWE users into the DID onboarding wizard on trade.
  const { isAuthed, walletConnected, canTrade } = useAuth();
  const { mismatch, boundWallet } = useBoundWalletGuard();
  const setLoginOpen = useModelStore(s => s.setLoginOpen);
  const setOnboardingOpen = useModelStore(s => s.setOnboardingOpen);

  return useCallback((): boolean => {
    // Not signed in at all → full login picker.
    if (!isAuthed) {
      setLoginOpen(true);
      return false;
    }
    // No proven wallet (DID session before link-wallet) or wallet not
    // currently connected → onboarding wizard (link/connect → fund → enable).
    if (!canTrade || !walletConnected) {
      setOnboardingOpen(true);
      return false;
    }
    // Bound wallet exists but a DIFFERENT wallet is connected (ADR-051 D2):
    // signing from it would split on-chain positions from the uid-keyed view.
    // The binding is immutable, so the only safe action is to switch back.
    if (mismatch) {
      toast.error(
        t('wrong-wallet', 'Wrong wallet connected. Switch to your linked wallet {{wallet}} to trade.', {
          wallet: truncateWallet(boundWallet),
        })
      );
      return false;
    }
    return true;
  }, [isAuthed, walletConnected, canTrade, mismatch, boundWallet, t, setLoginOpen, setOnboardingOpen]);
};
